import { ColorClassNames, ColorVariables } from "manifest-components/src/Colors";
import { Container, FlexContainer } from "manifest-components/src/Containers";
import { HamburgerMenu } from "manifest-components/src/Images/icons";
import React, { useContext, useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { Button } from "manifest-components/src/Button";
import { getMobileBreakpoint, globalCSSVars } from "manifest-components/src/Styles/globals";
import { ReactComponent as Logo } from "manifest-components/src/Images/icons/manifest.svg";
import StyledSVG from "manifest-components/src/Images/StyledSVG";
import Authentication from "../../utils/authentication";
import { AppContext } from "../../context/App/appProvider";
import Session from "../../utils/session";
import { route, RoutePaths, useTypedLocation, useTypedNavigate } from "../../routes/RoutePaths";
import TransferProgressBar, { useShowProgressBar } from "../TransferProgressBar/TransferProgressBar";
import Analytics, { HeapEventAction } from "../../utils/analytics";

const pathnameWithLoginButtons: string[] = [
  RoutePaths.welcome,
  RoutePaths.createAccount,
  RoutePaths.index,
  RoutePaths.changelog,
];

export const HeaderHeight = 80; // 120 for auth
export const HeaderAuthHeight = 120;
export const MobileHeaderHeight = 60;
export const HeaderDesktopMaxWidth = 1000;

const pulse = keyframes`
 50% {
  opacity: 0.5;
 `;

const StyledIconContainer = styled(FlexContainer)`
  cursor: pointer;
  background-color: transparent;

  flex: 1;
  svg {
    width: 31px;
    height: 31px;
  }
  @media (max-width: ${getMobileBreakpoint}) {
    svg {
      width: 25px;
      height: 20px;
    }
  }
`;

const MainContainer = styled(FlexContainer)`
  flex-direction: column;
  position: fixed;
  top: 0;
  z-index: 999;
  #headerLeftSideContent {
    display: flex;
    margin-right: auto;
    flex: 1;
  }
  #hamburgerIcon {
    position: absolute;
    top: 10px;
  }
`;

const StyledContainer = styled(FlexContainer)`
  justify-content: center;
  padding-top: 25px;
  width: 100%;
  @media (max-width: ${getMobileBreakpoint}) {
    padding-top: 20px;
  }
`;

const StyledGradient = styled.div<{ $show: boolean }>`
  display: ${(props) => !props.$show && "none"};
  background: linear-gradient(180deg, rgba(234, 226, 219, 0) 0%, rgba(0, 0, 0, 0.05) 70.3%);
  width: 100%;
  height: 20px;
  mix-blend-mode: multiply;
  transform: rotate(-180deg);
`;

const StyledLoginButton = styled.button`
  width: auto;

  @media (max-width: ${getMobileBreakpoint}) {
    width: 48px;
  }
`;

const StyledSignupButton = styled(Button)`
  width: 130px;
  margin-left: 50px;
  @media (max-width: ${getMobileBreakpoint}) {
    min-width: 80px;
    width: auto;
    font-size: 16px;
    line-height: 24px;
    margin-left: 10px;
  }
`;

const StyledGetStartedButton = styled(Button)`
  width: 160px;
  margin-left: 50px;
  @media (max-width: ${getMobileBreakpoint}) {
    min-width: 110px;
    width: auto;
    font-size: 16px;
    line-height: 24px;
    margin-left: 10px;
  }
`;

const LogoContainer = styled.button`
  display: flex;
  align-items: center;
  svg,
  img {
    max-height: 32px;
    max-width: 200px;
    object-fit: contain;
  }
  .skeletonLogoDiv {
    width: 180px;
    height: 32px;
    margin-right: 30px;
    background: ${ColorVariables.coco3};
    border-radius: 12px;
    animation: ${pulse} 1.25s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
  .vendorLogoDiv {
    display: flex;
    align-items: center;
  }
  .manifestLogo {
    height: 30px;
    width: 100px;
  }
  .logoSeparator {
    margin-left: 20px;
    margin-right: 20px;
    width: 1px;
    height: 30px;
    background: ${ColorVariables.coco3};
  }
  @media (max-width: ${getMobileBreakpoint}) {
    width: 180px;
    margin-top: 3px;
    svg,
    img {
      object-fit: contain;
      max-width: 70px;
      max-height: 20px;
      width: auto;
      height: auto;
    }
    .manifestLogo {
      height: 16px;
    }
    .logoSeparator {
      margin-left: 13px;
      margin-right: 0px;
      height: 23px;
    }
  }
`;

type HeaderProps = {
  vendorLogo?: string;
  minWidth?: number;
  showSignup?: boolean;
  onLogin?: () => any;
  onSignup?: (args: any) => any;
  onLogoClicked?: (args?: any) => any;
  shouldShowLogin?: boolean;
  signUpText?: string;
};

const GlobalHeaderStyles = createGlobalStyle<{ showProgressBar: boolean }>`
  #app-container {
    padding-top: ${(props) => {
      if (!props.showProgressBar) return "40px";
      return "80px";
    }}
  }
  @media (max-width: ${getMobileBreakpoint}) {
    #app-container {
      padding-top: 0;
    }
  }
`;

const StyledHeaderWrapper = styled(FlexContainer)`
  width: 100%;
  padding-left: 20px;
  padding-right: 20px;
  max-width: 1260px;
  height: 40px;
  @media (min-width: 769px) {
    padding-left: 40px;
    padding-right: 40px;
  }
`;

const Header = (props: HeaderProps) => {
  const location = useTypedLocation();
  const params = useParams();
  const navigate = useTypedNavigate();
  const {
    minWidth = globalCSSVars.mobileWidth,
    onLogin,
    onSignup,
    showSignup = false,
    onLogoClicked,
    shouldShowLogin = true,
    signUpText = "Sign up",
    vendorLogo,
  } = props;
  const { openHamburgerMenu, showTopBarIcons, showTopBarGradient } = useContext(AppContext);
  const shouldShowProgressBar = useShowProgressBar();
  const [scrollPosition, setScrollPosition] = useState(0);
  const [vendorLogoUrl, setVendorLogoUrl] = useState<string | undefined>(vendorLogo);
  const [isLogoLoaded, setIsLogoLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (vendorLogo) {
      setVendorLogoUrl(vendorLogo);
      const logoDiv = document.getElementById("vendorLogo");
      if (logoDiv) {
        logoDiv.addEventListener("load", () => {
          setIsLogoLoaded(true);
        });
      }
    }
  }, [vendorLogo]);

  const handleScroll = () => {
    setScrollPosition(window.pageYOffset);
  };

  const updateScrollPosition = () => {
    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });
  };
  updateScrollPosition();

  const showMenuIcon =
    Authentication.verify() && showTopBarIcons.showHamburger && !location.pathname.includes("loading");

  const showLogin =
    showTopBarIcons.showHamburger &&
    (pathnameWithLoginButtons.includes(location.pathname) || params.vendorName) &&
    !Authentication.verify();

  /*
   * showTopBarGradient : Coming from AppContext : Can be set to true/false from a screen when necessary
   * employerSearch : Don't show when on route /employerSearch
   * scrollPosition : We only show gradient on scroll
   */
  const showGradient = showTopBarGradient && !location.pathname.includes("employerSearch") && scrollPosition > 0;

  /*
   * For the top bar, do not apply the background color to the top bar when we don;t show the gradient.
   * Except for the /employerSearch route : For this route we show background even without the gradient
   */
  const showBackground =
    showGradient || location.pathname.includes("employerSearch") || location.pathname.includes("IRAOptions");

  const handleSignup = () => {
    if (onSignup) return onSignup("Sign up");
    navigate(route(RoutePaths.createAccount));
  };

  const handleLogin = () => {
    if (location.pathname.includes("brose")) {
      Analytics.track({ action: HeapEventAction.clicked, buttonName: "Log In", screen: "Brose Welcome Page" });
    }
    if (onLogin) return onLogin();
    navigate(route(RoutePaths.login));
  };

  const HamburgerMenuIcon = () => (
    <Container role="button" data-name="hamburger" onClick={showMenuIcon ? openHamburgerMenu : undefined}>
      {showMenuIcon && <HamburgerMenu id="hamburgerIcon" />}
    </Container>
  );

  const logoClickHandler = () => {
    if (onLogoClicked) {
      onLogoClicked();
    }
    const path = Session.vendorName
      ? route(RoutePaths.welcomeWithVendor, { vendorName: Session.vendorName })
      : route(RoutePaths.index);
    navigate(path);
  };

  const headerDesktopHeight = useMemo(() => {
    if (shouldShowProgressBar) {
      return HeaderAuthHeight;
    }
    return HeaderHeight;
  }, [shouldShowProgressBar]);

  return (
    <>
      <GlobalHeaderStyles showProgressBar={shouldShowProgressBar} />
      <MainContainer id="header" className="fullWidth">
        <StyledContainer
          style={{
            background: showBackground ? ColorVariables.background : "inherit",
            height: headerDesktopHeight,
            minWidth,
          }}
          $alignItems="baseline"
          $mobileMinWidth={0}
          $mobileHeight={MobileHeaderHeight}
          className="headerContent fullWidth"
        >
          <StyledHeaderWrapper>
            <div id="headerLeftSideContent">
              {showLogin && (
                <LogoContainer onClick={logoClickHandler}>
                  <div id="vendorLogoDiv" className="vendorLogoDiv">
                    <img
                      src={vendorLogoUrl || ""}
                      alt="vendor logo"
                      style={{ display: isLogoLoaded ? "block" : "none" }}
                      id="vendorLogo"
                    />
                    {vendorLogoUrl && isLogoLoaded && <div className="logoSeparator" />}
                    <div
                      className="skeletonLogoDiv"
                      style={{
                        display: !vendorLogo || isLogoLoaded ? "none" : "block",
                      }}
                    />
                  </div>
                  {/* {props.vendorLogo && (
                    <>
                      <props.vendorLogo />
                 
                    </>
                  )} */}
                  <StyledSVG fill={ColorVariables.coco} className="manifestLogo" icon={Logo} alt="Manifest Logo" />
                </LogoContainer>
              )}
              <div id="navBackIconPortal" data-name="navBackIconPortal" />
              {shouldShowProgressBar && <TransferProgressBar />}
            </div>

            <FlexContainer $alignItems="center" $justifyContent="flex-end">
              {showLogin && shouldShowLogin && (
                <StyledLoginButton data-name="login" onClick={handleLogin}>
                  <p className={`medium bold ${ColorClassNames.mint}`}>Log in</p>
                </StyledLoginButton>
              )}
              {showSignup ? (
                signUpText === "Get started" ? (
                  <StyledGetStartedButton name="signUp" className="small" onClick={handleSignup} text="Get started" />
                ) : showSignup ? (
                  <StyledSignupButton name="signUp" className="small" onClick={handleSignup} text={signUpText} />
                ) : (
                  <></>
                )
              ) : (
                <></>
              )}
            </FlexContainer>
            {showMenuIcon && (
              <StyledIconContainer>
                <HamburgerMenuIcon />{" "}
              </StyledIconContainer>
            )}
          </StyledHeaderWrapper>
        </StyledContainer>
        <StyledGradient $show={showGradient} />
      </MainContainer>
    </>
  );
};

export default function HeaderComponent(routeProps: any) {
  return <Header {...routeProps} />;
}
